import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./CustomModal.module.scss";

interface ModalButtonProps {
  label: string;
  type?: "text" | "outlined" | "contained";
  onClick?: () => void;
  color?:
    | "primary"
    | "secondary"
    | "inherit"
    | "error"
    | "info"
    | "success"
    | "warning";
  disabled?: boolean;
}

interface CustomModalProps {
  open: boolean;
  closeModal: () => void;
  title: string;
  cancelButton?: ModalButtonProps;
  confirmButton?: ModalButtonProps;
  children: () => React.ReactNode;
  closeOnSubmit?: boolean;
  sx?: SxProps<Theme>;
}

const CustomModal: React.FC<CustomModalProps> = ({
  open,
  closeModal,
  title,
  cancelButton,
  confirmButton,
  children,
  closeOnSubmit = true,
  sx,
}) => {
  const handleButtonClick = (action?: () => void) => {
    if (action) action();
    if (closeOnSubmit) closeModal();
  };

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        className={styles.modal}
        PaperProps={{ sx }}
      >
        <DialogTitle className={styles.title}>
          {title}
          <IconButton
            aria-label="close"
            className={styles.close}
            onClick={closeModal}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>{children()}</DialogContent>

        <DialogActions className={styles.action}>
          {cancelButton && (
            <Button
              variant={cancelButton.type || "text"}
              color={cancelButton.color || "inherit"}
              onClick={() => handleButtonClick(cancelButton.onClick)}
              disabled={cancelButton.disabled}
            >
              {cancelButton.label}
            </Button>
          )}

          {confirmButton && (
            <Button
              variant={confirmButton.type || "contained"}
              color={confirmButton.color || "primary"}
              onClick={() => handleButtonClick(confirmButton.onClick)}
              disabled={confirmButton.disabled}
            >
              {confirmButton.label}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomModal;
